import { CheckOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { App, Button, Checkbox, Select, Space, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import {
  batchUpdateSharePermission,
  getSharedFolders,
  revokeShare,
  type SharedFolder
} from '../api/share';
import { useTranslation } from 'react-i18next';

const ShareManagement: React.FC = () => {
  const { modal, message } = App.useApp();
  const [shares, setShares] = useState<SharedFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedShareIds, setSelectedShareIds] = useState<number[]>([]);
  const [batchPermission, setBatchPermission] = useState<'read' | 'write'>('read');
  const { t } = useTranslation('common');

  // Load shared folders
  const loadShares = async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const response = await getSharedFolders({ page, page_size: size });
      if (response?.ok) {
        setShares(response.data.shares);
        setTotal(response.data.total);
        setCurrentPage(response.data.page);
        setPageSize(response.data.page_size);
      } else {
        message.error(t('share_management.load_failed'));
      }
    } catch (error) {
      console.error('Error loading shares:', error);
      message.error(t('share_management.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShares();
  }, []);

  // Handle revoke share
  const handleRevokeShare = (shareId: number, filePath: string) => {
    modal.confirm({
      title: t('share_management.confirm_revoke_title'),
      content: t('share_management.revoke_confirm_content', {
        path: filePath,
        interpolation: { escapeValue: false },
      }),
      okText: t('share_management.confirm'),
      cancelText: t('share_management.cancel'),
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await revokeShare({ shareId });
          if (response?.ok) {
            message.success(t('share_management.revoke_success'));
            loadShares(currentPage, pageSize);
          } else {
            message.error(response?.message || t('share_management.revoke_failed'));
          }
        } catch (error) {
          console.error('Error revoking share:', error);
          message.error(t('share_management.revoke_failed'));
        }
      }
    });
  };


  // Get status tag color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'processing';
      case 'revoked':
        return 'error';
      case 'expired':
        return 'default';
      default:
        return 'default';
    }
  };

  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return t('share_management.status_active');
      case 'pending':
        return t('share_management.status_pending');
      case 'revoked':
        return t('share_management.status_revoked');
      case 'expired':
        return t('share_management.status_expired');
      default:
        return status;
    }
  };

  // Get permission text
  const getPermissionText = (permission: string) => {
    switch (permission) {
      case 'read':
        return t('share_management.perm_read');
      case 'write':
        return t('share_management.perm_write');
      case 'full':
        return t('share_management.perm_full');
      default:
        return permission;
    }
  };

  // Get permission tag color
  const getPermissionColor = (permission: string) => {
    switch (permission) {
      case 'read':
        return 'blue';
      case 'write':
        return 'orange';
      case 'full':
        return 'red';
      default:
        return 'default';
    }
  };

  // Handle batch mode toggle
  const handleBatchModeToggle = () => {
    setBatchMode(!batchMode);
    setSelectedShareIds([]);
  };

  // Handle checkbox change
  const handleCheckboxChange = (shareId: number, checked: boolean) => {
    if (checked) {
      setSelectedShareIds([...selectedShareIds, shareId]);
    } else {
      setSelectedShareIds(selectedShareIds.filter(id => id !== shareId));
    }
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const activeShareIds = shares
        .filter(share => share.status !== 'revoked' && share.status !== 'expired')
        .map(share => share.id);
      setSelectedShareIds(activeShareIds);
    } else {
      setSelectedShareIds([]);
    }
  };

  // Handle batch permission update
  const handleBatchPermissionUpdate = async () => {
    if (selectedShareIds.length === 0) {
      message.warning(t('share_management.select_share_warn'));
      return;
    }

    try {
      const response = await batchUpdateSharePermission({
        shareIds: selectedShareIds,
        share_type: batchPermission
      });
      if (response?.ok) {
        const { results, success_count, fail_count } = response.data;
        
        // Show success message if all succeeded
        if (fail_count === 0) {
          message.success(response.message || t('share_management.update_success', { count: success_count }));
          setSelectedShareIds([]);
          setBatchMode(false);
          loadShares(currentPage, pageSize);
        } else {
          // Show partial success and display failures in modal
          const failedResults = results.filter(r => !r.success);
          const shareIdToPath = shares.reduce((acc, share) => {
            acc[share.id] = share.file_path;
            return acc;
          }, {} as Record<number, string>);
          
          modal.warning({
            title: t('share_management.update_results', { success: success_count, fail: fail_count }),
            width: 600,
            content: (
              <div className="space-y-1">
                {failedResults.map((result, index) => (
                  <div key={index} className="text-sm">
                    <span className="font-medium">{shareIdToPath[result.share_id] || `ID: ${result.share_id}`}</span>
                    <span className="text-red-600 dark:text-[#F48771]">: {result.message}</span>
                  </div>
                ))}
              </div>
            ),
            onOk: () => {
              setSelectedShareIds([]);
              setBatchMode(false);
              loadShares(currentPage, pageSize);
            }
          });
        }
      } else {
        message.error(response?.message || t('share_management.update_failed'));
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
      message.error(t('share_management.update_failed'));
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US');
  };

  const columns: ColumnsType<SharedFolder> = [
    ...(batchMode ? [{
      title: (
        <Checkbox
          indeterminate={selectedShareIds.length > 0 && selectedShareIds.length < shares.filter(s => s.status !== 'revoked' && s.status !== 'expired').length}
          checked={selectedShareIds.length > 0 && selectedShareIds.length === shares.filter(s => s.status !== 'revoked' && s.status !== 'expired').length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: 'checkbox',
      width: 50,
      fixed: 'left' as const,
      render: (_: any, record: SharedFolder) => (
        <Checkbox
          checked={selectedShareIds.includes(record.id)}
          onChange={(e) => handleCheckboxChange(record.id, e.target.checked)}
          disabled={record.status === 'revoked' || record.status === 'expired'}
        />
      ),
    }] : []),
    {
      title: t('share_management.folder_col'),
      dataIndex: 'file_path',
      key: 'file_path',
      ellipsis: true,
      render: (path: string) => (
        <Space>
          <Tooltip title={path}>
            <span>{path}</span>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: t('share_management.shared_with_col'),
      dataIndex: 'target_user',
      key: 'target_user',
      width: 150,
    },
    {
      title: t('share_management.permission_col'),
      dataIndex: 'share_type',
      key: 'share_type',
      width: 180,
      render: (permission: string) => {
        return (
          <Tag color={getPermissionColor(permission)}>
            {getPermissionText(permission)}
          </Tag>
        );
      },
    },
    {
      title: t('share_management.status_col'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    // {
    //   title: 'Access Count',
    //   dataIndex: 'accessed_count',
    //   key: 'accessed_count',
    //   width: 120,
    // },
    {
      title: t('share_management.created_at_col'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: formatDate,
    },
    // {
    //   title: 'Expires At',
    //   dataIndex: 'expires_at',
    //   key: 'expires_at',
    //   width: 180,
    //   render: (expiresAt: string | null) => 
    //     expiresAt ? formatDate(expiresAt) : <Tag>Never</Tag>,
    // },
    // {
    //   title: 'Last Accessed',
    //   dataIndex: 'last_accessed_at',
    //   key: 'last_accessed_at',
    //   width: 180,
    //   render: formatDate,
    // },
    {
      title: t('share_management.actions_col'),
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_: any, record: SharedFolder) => (
        <Space>
          <Tooltip title={t('share_management.revoke_tooltip')}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleRevokeShare(record.id, record.file_path)}
              disabled={record.status === 'revoked' || record.status === 'expired'}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className=' w-full px-4 py-3'>
      <div className=' flex items-center justify-between mb-4'>
        <h2 className=' text-md font-semibold'>{t('share_management.my_shares')}</h2>
        <Space>
          <Button 
            icon={<CheckOutlined />} 
            onClick={handleBatchModeToggle}
            type={batchMode ? 'primary' : 'default'}
          >
            {batchMode ? t('share_management.cancel_batch') : t('share_management.batch_update')}
          </Button>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => loadShares(currentPage, pageSize)}
            loading={loading}
          >
            {t('share_management.refresh')}
          </Button>
        </Space>
      </div>
      {batchMode && (
        <div className='mb-4 p-4 bg-blue-50 dark:bg-[#1E3A5F] rounded-lg flex items-center justify-between'>
          <Space>
            <span className='font-medium'>{t('share_management.selected', { count: selectedShareIds.length })}</span>
            <Select
              value={batchPermission}
              onChange={setBatchPermission}
              style={{ width: 150 }}
            >
              <Select.Option value="read">{t('share_management.read_only')}</Select.Option>
              <Select.Option value="write">{t('share_management.read_write')}</Select.Option>
            </Select>
          </Space>
          <Button 
            type="primary" 
            onClick={handleBatchPermissionUpdate}
            disabled={selectedShareIds.length === 0}
          >
            {t('share_management.update_permissions')}
          </Button>
        </div>
      )}
      <Table
        columns={columns}
        dataSource={shares}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1500 }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showTotal: (total) => t('share_management.show_total', { total }),
          onChange: (page, size) => {
            loadShares(page, size);
          },
        }}
      />
    </div>
  );
};

export default ShareManagement;
